import React from 'react';
import { Box, CircularProgress, Typography, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { ClaimStage } from '../../../interfaces/billing';

const StatusContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const StatusIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const StatusMessage = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const TimestampText = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(1),
}));

interface ProcessStatusIndicatorProps {
  status: ClaimStage;
  message?: string;
  timestamp?: string;
  size?: 'small' | 'medium' | 'large';
  showMessage?: boolean;
  showTimestamp?: boolean;
  className?: string;
}

/**
 * Component for displaying process status with appropriate icons and messages
 */
export const ProcessStatusIndicator: React.FC<ProcessStatusIndicatorProps> = ({
  status,
  message,
  timestamp,
  size = 'medium',
  showMessage = true,
  showTimestamp = false,
  className,
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case ClaimStage.CreationInProgress:
        return {
          icon: <CircularProgress size={getIconSize()} color="primary" />,
          color: 'primary',
          defaultMessage: 'Creating claims...',
          tooltip: 'Claims are being created in the background',
        };
      case ClaimStage.Created:
        return {
          icon: <CheckCircleIcon fontSize={getIconSize()} color="success" />,
          color: 'success',
          defaultMessage: 'Claims created',
          tooltip: 'Claims have been successfully created',
        };
      case ClaimStage.ExportInProgress:
        return {
          icon: <CircularProgress size={getIconSize()} color="secondary" />,
          color: 'secondary',
          defaultMessage: 'Exporting claims...',
          tooltip: 'Claims are being exported to external systems',
        };
      case ClaimStage.Exported:
        return {
          icon: <CheckCircleIcon fontSize={getIconSize()} color="success" />,
          color: 'success',
          defaultMessage: 'Claims exported',
          tooltip: 'Claims have been successfully exported',
        };
      case ClaimStage.Discarded:
        return {
          icon: <ErrorIcon fontSize={getIconSize()} color="error" />,
          color: 'error',
          defaultMessage: 'Process cancelled',
          tooltip: 'Process was cancelled or failed',
        };
      default:
        return {
          icon: <AccessTimeIcon fontSize={getIconSize()} color="disabled" />,
          color: 'text.secondary',
          defaultMessage: 'Unknown status',
          tooltip: 'Status information not available',
        };
    }
  };

  const getIconSize = (): 'small' | 'medium' | 'large' => {
    switch (size) {
      case 'small':
        return 'small';
      case 'large':
        return 'large';
      default:
        return 'medium';
    }
  };

  const getIconSizeValue = (): number => {
    switch (size) {
      case 'small':
        return 16;
      case 'large':
        return 32;
      default:
        return 24;
    }
  };

  const statusConfig = getStatusConfig();
  const displayMessage = message || statusConfig.defaultMessage;

  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      
      if (diffInMinutes < 1) {
        return 'Just now';
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}m ago`;
      } else if (diffInMinutes < 1440) {
        return `${Math.floor(diffInMinutes / 60)}h ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch (error) {
      return '';
    }
  };

  const content = (
    <StatusContainer className={className}>
      <StatusIcon>
        {React.cloneElement(statusConfig.icon, {
          size: statusConfig.icon.type === CircularProgress ? getIconSizeValue() : undefined,
        })}
      </StatusIcon>
      
      {showMessage && (
        <StatusMessage 
          color={statusConfig.color}
          variant={size === 'small' ? 'caption' : 'body2'}
        >
          {displayMessage}
        </StatusMessage>
      )}
      
      {showTimestamp && timestamp && (
        <TimestampText variant="caption">
          {formatTimestamp(timestamp)}
        </TimestampText>
      )}
    </StatusContainer>
  );

  if (statusConfig.tooltip) {
    return (
      <Tooltip title={statusConfig.tooltip} placement="top">
        {content}
      </Tooltip>
    );
  }

  return content;
};

/**
 * Utility function to check if a status is in active/processing state
 */
export const isActiveStatus = (status: ClaimStage): boolean => {
  return [
    ClaimStage.CreationInProgress,
    ClaimStage.ExportInProgress,
  ].includes(status);
};

/**
 * Utility function to check if a status is in completed state
 */
export const isCompletedStatus = (status: ClaimStage): boolean => {
  return [
    ClaimStage.Created,
    ClaimStage.Exported,
  ].includes(status);
};

/**
 * Utility function to check if a status is in error state
 */
export const isErrorStatus = (status: ClaimStage): boolean => {
  return status === ClaimStage.Discarded;
};

/**
 * Utility function to get status priority for sorting
 */
export const getStatusPriority = (status: ClaimStage): number => {
  const priorities = {
    [ClaimStage.CreationInProgress]: 1,
    [ClaimStage.ExportInProgress]: 2,
    [ClaimStage.Created]: 3,
    [ClaimStage.Exported]: 4,
    [ClaimStage.Discarded]: 5,
  };
  
  return priorities[status] || 99;
};

export default ProcessStatusIndicator;